import { arrayMove } from "@dnd-kit/sortable"
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { Hauler } from "src/db/Hauler"
import { HaulerEmployee } from "src/db/HaulerEmployee"
import { Order, OrderViewModel } from "src/db/Order"
import {
  DeliveryConstraintEnum,
  OrderLayoutOptionsEnum,
} from "src/features/config-setting/constants"
import { OrderCardLayoutConfig } from "src/features/config-setting/models"
import { OrderStatusEnum } from "src/features/shared-module/constants"
import {
  employeeSortingFunction,
  getEmployeeOrders,
  getOneLineAddress,
  getPresetColor,
  isDeliveryOnTime,
} from "src/features/shared-module/functions"
import { RouteSummary } from "src/features/shared-module/models"
import { RootState } from "../apis"

export interface AssignedEmployeeOrderDetail {
  grouped: { [routeSummaryId: number]: Order[] }
  individual: Order[]
  onTimeOrders: number
  delayedOrders: number
}

export interface RecentPosition {
  latitude: number
  longitude: number
  timestamp: string
}

export interface CourierPosition {
  haulerEmployeeId: number
  haulerEmployeeUuid: string
  latitude: number
  longitude: number
  heading: number
  speed: number
  accuracy: number
  altitude: number
  odometer: number
  batteryLevel: number
  timestamp: string
  recent: RecentPosition[]
}

const initialState: {
  orderList: OrderViewModel[]
  orderMasterList: OrderViewModel[]
  orderMap: Record<number, OrderViewModel>
  orderRouteSummaryMap: {
    [key: number]: {
      routeDetail: RouteSummary | null
      isLoading: boolean
      error?: boolean
    }
  }
  employeeList: HaulerEmployee[]
  employeeMasterList: HaulerEmployee[]
  haulerList: any[]
  orderCardLayoutConfig: OrderCardLayoutConfig | null
  isLoading: boolean
  isOrdersLoading: boolean
  haulerDetailMap: { [key: string]: Hauler }
  orderAnimateId: number | null
  providerAnimateId: number | null
  employeeOrders: {
    [haulerEmployeeId: number]: AssignedEmployeeOrderDetail
  }
  employeeDetailVisibility: {
    [haulerEmployeeId: number]: any
  }
  activeDragItem: OrderViewModel | null
  orderListByState: {
    ON_THE_WAY_TO_CUSTOMER: OrderViewModel[]
    ON_THE_WAY_TO_PROVIDER: OrderViewModel[]
    COMPLETED: OrderViewModel[]
    READY_FOR_HAULAGE: OrderViewModel[]
  }
  selectedOrderIds: number[]
  batchAssignInProgress: boolean
  scrollingDetail: Order | null
  courierPositions: { [haulerEmployeeId: number]: CourierPosition }
  expandableSections: {
    order: boolean
    employee: boolean
  }
} = {
  orderList: [],
  orderMasterList: [],
  orderMap: {},
  orderRouteSummaryMap: {},
  employeeList: [],
  employeeMasterList: [],
  haulerList: [],
  orderCardLayoutConfig: {
    /* TODO: remove when api exist */
    deliveryConstraint: DeliveryConstraintEnum.TEXT_VERSION,
    topLeft: OrderLayoutOptionsEnum.reference,
    topRight: OrderLayoutOptionsEnum.clientName,
    bottomLeft: OrderLayoutOptionsEnum.version,
    bottomRight: OrderLayoutOptionsEnum.amount,
    id: null,
  },
  isLoading: false,
  isOrdersLoading: false,
  haulerDetailMap: {},
  orderAnimateId: null,
  providerAnimateId: null,
  employeeOrders: {},
  employeeDetailVisibility: {},
  activeDragItem: null,
  orderListByState: {
    ON_THE_WAY_TO_CUSTOMER: [],
    ON_THE_WAY_TO_PROVIDER: [],
    COMPLETED: [],
    READY_FOR_HAULAGE: [],
  },
  selectedOrderIds: [],
  batchAssignInProgress: false,
  scrollingDetail: null,
  courierPositions: {},
  expandableSections: {
    order: false,
    employee: false,
  },
}
export const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    setExpandableSections: (
      state,
      action: PayloadAction<"order" | "employee">,
    ) => {
      state.expandableSections[action.payload] =
        !state.expandableSections[action.payload]
    },
    setOrderRoutes: (
      state,
      action: PayloadAction<{
        routeSummaryId: number | null
        routeDetail: RouteSummary | null
        isLoading: boolean
        error?: boolean
      }>,
    ) => {
      const { routeSummaryId, routeDetail, isLoading, error } = action.payload

      if (routeSummaryId) {
        state.orderRouteSummaryMap[routeSummaryId] = {
          routeDetail,
          isLoading,
          error,
        }
      } else {
        state.orderRouteSummaryMap = {}
      }
    },
    setOrderist: (state, action: PayloadAction<Order[]>) => {
      const orderList = action.payload.map((order) => {
        return {
          ...order,
          oneLineAddress: getOneLineAddress(order.address),
          allTagList: [
            ...(order.client?.tags ?? []).map((e) => e.tag),
            ...(order.tags ?? []).map((e) => e.tag),
          ],
          isOnTimeDelivery: isDeliveryOnTime(order),
        }
      })
      // Shallow copy is sufficient — .map() already created fresh objects
      state.orderMasterList = [...orderList]

      state.employeeOrders = getEmployeeOrders(
        state.orderMasterList,
        state.employeeList,
      )

      state.orderList = orderList

      // Single-pass bucketing by state + build orderMap
      const byState = {
        ON_THE_WAY_TO_CUSTOMER: [] as OrderViewModel[],
        ON_THE_WAY_TO_PROVIDER: [] as OrderViewModel[],
        COMPLETED: [] as OrderViewModel[],
        READY_FOR_HAULAGE: [] as OrderViewModel[],
      }
      const orderMap: Record<number, OrderViewModel> = {}
      for (const order of orderList) {
        orderMap[order.id] = order
        if (order.state === OrderStatusEnum.ON_THE_WAY_TO_CUSTOMER) {
          byState.ON_THE_WAY_TO_CUSTOMER.push(order)
        } else if (order.state === OrderStatusEnum.ON_THE_WAY_TO_PROVIDER) {
          byState.ON_THE_WAY_TO_PROVIDER.push(order)
        } else if (order.state === OrderStatusEnum.COMPLETED) {
          byState.COMPLETED.push(order)
        } else if (order.state === OrderStatusEnum.READY_FOR_HAULAGE) {
          byState.READY_FOR_HAULAGE.push(order)
        }
      }
      state.orderListByState = byState
      state.orderMap = orderMap
    },
    setEmployeeList: (state, action: PayloadAction<HaulerEmployee[]>) => {
      state.employeeList = action.payload.map((e, index) => {
        return {
          ...e,
          color: getPresetColor(index),
        }
      })
      state.employeeMasterList = [...state.employeeList]
    },
    setEmployeeColor: (
      state,
      action: PayloadAction<{ id: number; color: string }>,
    ) => {
      const employee = state.employeeList.find(
        (e) => e.id === action.payload.id,
      )
      if (employee) {
        employee.color = action.payload.color
      }
    },
    setOrderCardLayoutConfig: (
      state,
      action: PayloadAction<OrderCardLayoutConfig>,
    ) => {
      state.orderCardLayoutConfig = action.payload
    },
    setHaulerList: (state, action: PayloadAction<Hauler[]>) => {
      state.haulerList = action.payload
      state.haulerDetailMap = action.payload.reduce(
        (acc: Record<string, Hauler>, hauler) => {
          acc[hauler.id] = hauler
          return acc
        },
        {},
      )
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setOrdersLoading: (state, action: PayloadAction<boolean>) => {
      state.isOrdersLoading = action.payload
    },
    setActiveDragItem: (state, action: PayloadAction<number | null>) => {
      const activeOrderId = action.payload
      state.activeDragItem = activeOrderId
        ? state.orderMap[activeOrderId] ?? null
        : null
    },
    setOrderAnimateId: (state, action: PayloadAction<number | null>) => {
      state.orderAnimateId = action.payload
    },
    setProviderAnimateId: (state, action: PayloadAction<number | null>) => {
      state.providerAnimateId = action.payload
    },
    reorderInSameSection: (state, action: PayloadAction<any>) => {
      const { fromContainerId, toContainerId, fromOrderId, toOrderId } =
        action.payload
      const { haulerEmployeeId, routeSummaryId } =
        GetOrderDetailFromId(toContainerId)

      let items: Order[] = []
      if (toContainerId.includes("READY_FOR_HAULAGE")) {
        // TODO: add constant for id READY_FOR_HAULAGE
        items = state.orderListByState.READY_FOR_HAULAGE
      } else if (routeSummaryId) {
        items = state.employeeOrders[haulerEmployeeId].grouped[routeSummaryId]
      } else if (haulerEmployeeId) {
        items = state.employeeOrders[haulerEmployeeId].individual
      }

      const reorderedList = reorderSortabaleItems(items, fromOrderId, toOrderId)
      if (toContainerId.includes("READY_FOR_HAULAGE")) {
        state.orderListByState.READY_FOR_HAULAGE = reorderedList as any[]
      } else if (routeSummaryId) {
        state.employeeOrders[haulerEmployeeId].grouped[routeSummaryId] =
          reorderedList
      } else if (haulerEmployeeId) {
        state.employeeOrders[haulerEmployeeId].individual = reorderedList
      }
      state.activeDragItem = null
    },
    moveOrder: (state, action: PayloadAction<any>) => {
      const { fromContainerId, toContainerId, fromOrderId, toOrderId } =
        action.payload

      const {
        haulerEmployeeId: fromHaulerEmployeeId,
        routeSummaryId: fromRouteSummaryId,
      } = GetOrderDetailFromId(fromContainerId)

      const {
        haulerEmployeeId: toHaulerEmployeeId,
        routeSummaryId: toRouteSummaryId,
      } = GetOrderDetailFromId(toContainerId)

      let fromItems: Order[] = []
      if (fromContainerId.includes("READY_FOR_HAULAGE")) {
        fromItems = state.orderListByState.READY_FOR_HAULAGE
      } else if (fromRouteSummaryId) {
        fromItems =
          state.employeeOrders[fromHaulerEmployeeId].grouped[fromRouteSummaryId]
      } else if (fromHaulerEmployeeId) {
        fromItems = state.employeeOrders[fromHaulerEmployeeId].individual
      }

      let toItems: Order[] = []
      if (toContainerId.includes("READY_FOR_HAULAGE")) {
        toItems = state.orderListByState.READY_FOR_HAULAGE
      } else if (toRouteSummaryId) {
        toItems =
          state.employeeOrders[toHaulerEmployeeId].grouped[toRouteSummaryId]
      } else if (toHaulerEmployeeId) {
        toItems = state.employeeOrders[toHaulerEmployeeId].individual
      }

      const fromIndex = fromItems.findIndex((order) => order.id === fromOrderId)
      const toIndex = toItems.findIndex((order) => order.id === toOrderId)

      const finalFromList = fromItems.filter((e) => e.id !== fromOrderId)
      if (fromContainerId.includes("READY_FOR_HAULAGE")) {
        state.orderListByState.READY_FOR_HAULAGE = finalFromList as any[]
      } else if (fromRouteSummaryId) {
        state.employeeOrders[fromHaulerEmployeeId].grouped[fromRouteSummaryId] =
          finalFromList
      } else if (fromHaulerEmployeeId) {
        state.employeeOrders[fromHaulerEmployeeId].individual = finalFromList
      }

      const finalToList = [
        ...toItems.slice(0, toIndex),
        fromItems[fromIndex],
        ...toItems.slice(toIndex, toItems.length),
      ]

      if (toContainerId.includes("READY_FOR_HAULAGE")) {
        state.orderListByState.READY_FOR_HAULAGE = finalToList as any[]
      } else if (toRouteSummaryId) {
        state.employeeOrders[toHaulerEmployeeId].grouped[toRouteSummaryId] =
          finalToList
      } else if (toHaulerEmployeeId) {
        state.employeeOrders[toHaulerEmployeeId].individual = finalToList
      }
    },

    setPartialVisibilityInMap: (
      state,
      action: PayloadAction<{
        haulerEmployeeId: number
        routeSummaryId: number | null
      }>,
    ) => {
      const { haulerEmployeeId, routeSummaryId } = action.payload
      const key = routeSummaryId || "individual"
      if (!state.employeeDetailVisibility[haulerEmployeeId]) {
        state.employeeDetailVisibility[haulerEmployeeId] = {}
      }
      const visibility = state.employeeDetailVisibility[haulerEmployeeId][key]
      state.employeeDetailVisibility[haulerEmployeeId][key] =
        typeof visibility === "boolean"
          ? !state.employeeDetailVisibility[haulerEmployeeId][key]
          : false
    },

    setVisibilityInMap: (
      state,
      action: PayloadAction<{
        haulerEmployeeId: number
        visibility: boolean
      }>,
    ) => {
      const { haulerEmployeeId, visibility } = action.payload
      const employeeOrderDetail = state.employeeOrders[haulerEmployeeId]
      const routeSummaryIds = Object.keys(employeeOrderDetail.grouped)
      const obj: { [key: string | number]: boolean } = {
        individual: visibility,
      }
      routeSummaryIds.forEach((routeSummaryId) => {
        obj[routeSummaryId] = visibility
      })
      state.employeeDetailVisibility[haulerEmployeeId] = obj
    },
    setScrollingDetail: (state, action: PayloadAction<Order | null>) => {
      state.scrollingDetail = action.payload
    },
    toggleSelectOrder: (
      state,
      action: PayloadAction<{ orderId: number; shiftKey?: boolean }>,
    ) => {
      const { orderId, shiftKey } = action.payload
      const idx = state.selectedOrderIds.indexOf(orderId)

      if (shiftKey && state.selectedOrderIds.length > 0) {
        // Range selection within the READY_FOR_HAULAGE list
        const rfhOrders = state.orderListByState.READY_FOR_HAULAGE
        const lastSelectedId =
          state.selectedOrderIds[state.selectedOrderIds.length - 1]
        const lastIdx = rfhOrders.findIndex((o) => o.id === lastSelectedId)
        const currentIdx = rfhOrders.findIndex((o) => o.id === orderId)
        if (lastIdx !== -1 && currentIdx !== -1) {
          const start = Math.min(lastIdx, currentIdx)
          const end = Math.max(lastIdx, currentIdx)
          const rangeIds = rfhOrders.slice(start, end + 1).map((o) => o.id)
          const merged = new Set([...state.selectedOrderIds, ...rangeIds])
          state.selectedOrderIds = Array.from(merged)
          return
        }
      }

      if (idx >= 0) {
        state.selectedOrderIds.splice(idx, 1)
      } else {
        state.selectedOrderIds.push(orderId)
      }
    },
    clearSelection: (state) => {
      state.selectedOrderIds = []
    },
    selectAllReadyForHaulage: (state) => {
      state.selectedOrderIds = state.orderListByState.READY_FOR_HAULAGE.map(
        (o) => o.id,
      )
    },
    setBatchAssignInProgress: (state, action: PayloadAction<boolean>) => {
      state.batchAssignInProgress = action.payload
    },
    updateCourierPosition: (state, action: PayloadAction<CourierPosition>) => {
      state.courierPositions[action.payload.haulerEmployeeId] = action.payload
    },
    batchUpdateCourierPositions: (
      state,
      action: PayloadAction<CourierPosition[]>,
    ) => {
      for (const pos of action.payload) {
        state.courierPositions[pos.haulerEmployeeId] = pos
      }
    },
    filterOrderDetail: (
      state,
      action: PayloadAction<{
        haulerIds: number[]
        haulerEmployeeIds: number[]
        query: string | null
        sortBy: string
        sortOrder: "asc" | "desc"
        stateList: OrderStatusEnum[]
      }>,
    ) => {
      const {
        haulerEmployeeIds,
        query,
        haulerIds,
        stateList,
        sortBy,
        sortOrder,
      } = action.payload

      const filteredOrderList: OrderViewModel[] =
        haulerIds.length ||
        haulerEmployeeIds.length ||
        query ||
        stateList.length
          ? state.orderMasterList.filter((order) => {
              const {
                client,
                oneLineAddress,
                haulerEmployeeId,
                tenantId,
                state,
              } = order

              if (
                haulerEmployeeIds.length &&
                (!haulerEmployeeId ||
                  !haulerEmployeeIds.includes(haulerEmployeeId))
              ) {
                return false
              }
              if (haulerIds.length && !haulerIds.includes(tenantId)) {
                return false
              }
              if (stateList.length && !stateList.includes(state)) {
                return false
              }
              if (!query) return true
              const lowercaseQuery = query.toLowerCase()
              return (
                (client?.firstName ?? "")
                  .toLowerCase()
                  .includes(lowercaseQuery) ||
                (client?.lastName ?? "")
                  .toLowerCase()
                  .includes(lowercaseQuery) ||
                (oneLineAddress ?? "").toLowerCase().includes(lowercaseQuery)
              )
            })
          : state.orderMasterList

      const filteredEmployeeList: HaulerEmployee[] = haulerEmployeeIds.length
        ? state.employeeMasterList.filter((haulerEmployee) =>
            haulerEmployeeIds.includes(haulerEmployee.id),
          )
        : state.employeeMasterList

      if (sortBy) {
        filteredEmployeeList.sort(employeeSortingFunction(sortBy, sortOrder))
      }
      state.employeeList = filteredEmployeeList
      state.employeeOrders = getEmployeeOrders(
        filteredOrderList,
        filteredEmployeeList,
      )
      state.orderList = filteredOrderList
    },
  },
})

export const {
  setOrderRoutes,
  setOrderist,
  setEmployeeList,
  setHaulerList,
  setOrderCardLayoutConfig,
  setLoading,
  setOrdersLoading,
  setOrderAnimateId,
  setProviderAnimateId,
  reorderInSameSection,
  moveOrder,
  setActiveDragItem,
  setEmployeeColor,
  setPartialVisibilityInMap,
  setVisibilityInMap,
  setScrollingDetail,
  setExpandableSections,
  updateCourierPosition,
  batchUpdateCourierPositions,
  filterOrderDetail,
  toggleSelectOrder,
  clearSelection,
  selectAllReadyForHaulage,
  setBatchAssignInProgress,
} = dashboardSlice.actions

export const dashboardState = (state: RootState) => state.dashboard

export const isLoadingSelector = createSelector(
  dashboardState,
  (state) => state.isLoading,
)

export const isOrdersLoadingSelector = createSelector(
  dashboardState,
  (state) => state.isOrdersLoading,
)

export const haulerListSelector = createSelector(
  dashboardState,
  (state) => state.haulerList,
)

export const haulerDetailMapSelector = createSelector(
  dashboardState,
  (state) => state.haulerDetailMap,
)

export const orderListByStateSelector = createSelector(
  dashboardState,
  (state) => state.orderListByState,
)

export const courierPositionsSelector = createSelector(
  dashboardState,
  (state) => state.courierPositions,
)

export const selectedOrderIdsSelector = createSelector(
  dashboardState,
  (state) => state.selectedOrderIds,
)

export const selectedOrderIdsSetSelector = createSelector(
  selectedOrderIdsSelector,
  (ids) => new Set(ids),
)

export const batchAssignInProgressSelector = createSelector(
  dashboardState,
  (state) => state.batchAssignInProgress,
)

export const orderMapSelector = createSelector(
  dashboardState,
  (state) => state.orderMap,
)

export const selectOrderById = (id: number) =>
  createSelector(dashboardState, (state) => state.orderMap[id])

const reorderSortabaleItems = (
  items: Order[],
  fromId: number,
  toId: number,
) => {
  if (!items) return items

  const fromIndex = items.findIndex((order) => order.id === fromId)
  const toIndex = items.findIndex((order) => order.id === toId)

  if (fromIndex === -1 || toIndex === -1) return items

  return arrayMove(items, fromIndex, toIndex)
}

const GetOrderDetailFromId = (
  containerId: string,
): {
  haulerEmployeeId: number
  routeSummaryId: number | null
} => {
  const [haulerEmployeeId, routeSummaryId] = containerId.split("-")
  return {
    haulerEmployeeId: Number(haulerEmployeeId),
    routeSummaryId: routeSummaryId ? Number(routeSummaryId) : null,
  }
}
